// src/components/certificacion/SimulacionForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, IconButton, Select, MenuItem,
  TextField, CircularProgress, Chip, Alert, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  AddOutlined,
  DeleteOutlineOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ErrorOutlined,
} from '@mui/icons-material';
import { emisionDTEService } from '../../services/emisionDTEService';
import { certificacionService } from '../../services/certificacionService';

const MIN_DOCS = 10;
const HOY = new Date().toISOString().slice(0, 10);

const FILA_VACIA = { tipo_dte: 33, glosa: '', fecha: HOY, referencia: null };
const REQUIERE_REF = [56, 61];

export default function SimulacionForm({ rutEmpresa, paso, onActualizado }) {
  const [mostrarForm, setMostrarForm] = useState(false);
  const [folios, setFolios] = useState([]);
  const [docsEmitidos, setDocsEmitidos] = useState([]);       // para selector de referencia T56/T61
  const [prevEmitidos, setPrevEmitidos] = useState([]);       // ya enviados al SII (filas bloqueadas)
  const [docs, setDocs] = useState(Array.from({ length: MIN_DOCS }, () => ({ ...FILA_VACIA })));
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  const cargarDatos = () => {
    emisionDTEService.foliosDisponibles(rutEmpresa)
      .then(data => setFolios(data.filter(f => f.disponibles > 0)))
      .catch(() => {});
    certificacionService.listarDocumentosEmitidos(rutEmpresa)
      .then(setDocsEmitidos)
      .catch(() => {});
    certificacionService.listarEmitidosSimulacion(rutEmpresa)
      .then(prev => {
        setPrevEmitidos(prev);
        // Ajustar filas nuevas: solo las que faltan para llegar al mínimo
        const faltan = Math.max(1, MIN_DOCS - prev.length);
        setDocs(Array.from({ length: faltan }, () => ({ ...FILA_VACIA })));
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (mostrarForm) cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarForm, rutEmpresa]);

  const tiposDisponibles = folios.length > 0 ? folios : [{ tipo_dte: 33, nombre: 'Factura Electrónica', disponibles: 0 }];

  const foliosPorTipo = {};
  for (const f of folios) foliosPorTipo[f.tipo_dte] = f.disponibles;

  const setDoc = (idx, field, val) => {
    setDocs(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const agregarFila = () => setDocs(prev => [...prev, { ...FILA_VACIA }]);

  const eliminarFila = (idx) => {
    if (docs.length <= 1) return;
    setDocs(prev => prev.filter((_, i) => i !== idx));
  };

  // Validaciones
  const usadosPorTipo = {};
  for (const d of docs) usadosPorTipo[d.tipo_dte] = (usadosPorTipo[d.tipo_dte] || 0) + 1;

  const erroresFila = docs.map(d => {
    if (!d.glosa.trim()) return 'La glosa es obligatoria';
    const disponibles = foliosPorTipo[d.tipo_dte];
    if (disponibles !== undefined && usadosPorTipo[d.tipo_dte] > disponibles)
      return `Folios insuficientes (disponibles: ${disponibles})`;
    if (REQUIERE_REF.includes(d.tipo_dte) && !d.referencia)
      return 'T56/T61 requiere documento de referencia';
    return null;
  });

  const hayErrores = erroresFila.some(Boolean);
  const totalDocs = prevEmitidos.length + docs.length;
  const faltanDocs = totalDocs < MIN_DOCS;

  const handleEmitir = async () => {
    setLoading(true);
    setError(null);
    setResultado(null);
    try {
      const res = await certificacionService.ejecutarSimulacion(
        rutEmpresa,
        docs.map(d => ({
          tipo_dte: d.tipo_dte,
          glosa: d.glosa,
          fecha: d.fecha,
          referencia: d.referencia || null,
        }))
      );
      setResultado(res);
      // Recargar prevEmitidos para reflejar los nuevos éxitos
      certificacionService.listarEmitidosSimulacion(rutEmpresa)
        .then(prev => {
          setPrevEmitidos(prev);
          const faltan = Math.max(1, MIN_DOCS - prev.length);
          setDocs(prev2 => prev2.filter(d => !res.documentos?.find(r => r.ok && r.glosa === d.glosa))
            .concat(
              Array.from({ length: Math.max(0, faltan - prev2.filter(d => !res.documentos?.find(r => r.ok && r.glosa === d.glosa)).length) }, () => ({ ...FILA_VACIA }))
            )
          );
        })
        .catch(() => {});
      onActualizado();
    } catch (err) {
      setError(err.message || 'Error al emitir documentos');
    } finally {
      setLoading(false);
    }
  };

  if (!mostrarForm) {
    return (
      <Button
        size="small"
        variant="contained"
        onClick={() => setMostrarForm(true)}
      >
        Configurar simulación
      </Button>
    );
  }

  return (
    <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, maxWidth: 700 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Set de Simulación — mínimo {MIN_DOCS} documentos representativos
      </Typography>

      {/* Folios disponibles */}
      {folios.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Folios disponibles:
          </Typography>
          {folios.map(f => (
            <Chip
              key={f.tipo_dte}
              label={`T${f.tipo_dte}: ${f.disponibles}`}
              size="small"
              variant="outlined"
              color={f.disponibles > 0 ? 'default' : 'error'}
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          ))}
        </Box>
      )}

      {/* Tabla de documentos */}
      <Box sx={{ mb: 1 }}>
        {/* Encabezado */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 130px 32px', gap: 0.5, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Tipo DTE</Typography>
          <Typography variant="caption" color="text.secondary">Glosa / Folio</Typography>
          <Typography variant="caption" color="text.secondary">Fecha</Typography>
          <span />
        </Box>

        {/* Filas ya emitidas (bloqueadas) */}
        {prevEmitidos.map((d, idx) => (
          <Box
            key={`prev-${idx}`}
            sx={{
              display: 'grid', gridTemplateColumns: '130px 1fr 130px 32px',
              gap: 0.5, mb: 0.5, alignItems: 'center',
              bgcolor: 'success.50', borderRadius: 0.5, px: 0.5,
              border: '1px solid', borderColor: 'success.200', opacity: 0.85,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label={`T${d.tipo_dte}`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.72rem', height: 20 }} />
            </Box>
            <Tooltip title={`TrackID: ${d.trackid}`} placement="top">
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'success.dark' }}>
                F{d.folio} · ${d.monto_total?.toLocaleString('es-CL')}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">{d.fecha_emision}</Typography>
            <CheckCircleOutlined sx={{ fontSize: 16, color: 'success.main' }} />
          </Box>
        ))}

        {/* Filas nuevas (editables) */}
        {docs.map((d, idx) => (
          <Box
            key={idx}
            sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 130px 32px', gap: 0.5, mb: 0.5, alignItems: 'flex-start' }}
          >
            <Select
              value={d.tipo_dte}
              size="small"
              onChange={e => setDoc(idx, 'tipo_dte', e.target.value)}
              sx={{ fontSize: '0.78rem', height: 32 }}
            >
              {tiposDisponibles.map(f => (
                <MenuItem key={f.tipo_dte} value={f.tipo_dte} disabled={f.disponibles === 0}>
                  T{f.tipo_dte} — {f.nombre}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size="small"
              value={d.glosa}
              onChange={e => setDoc(idx, 'glosa', e.target.value)}
              placeholder="Descripción del servicio o producto"
              error={!!erroresFila[idx]}
              helperText={erroresFila[idx]}
              inputProps={{ style: { fontSize: '0.78rem', padding: '4px 8px' } }}
            />

            <TextField
              size="small"
              type="date"
              value={d.fecha}
              onChange={e => setDoc(idx, 'fecha', e.target.value)}
              inputProps={{ style: { fontSize: '0.78rem', padding: '4px 8px' } }}
            />

            <Tooltip title="Eliminar fila">
              <span>
                <IconButton
                  size="small"
                  onClick={() => eliminarFila(idx)}
                  disabled={docs.length <= 1}
                  sx={{ p: 0.3 }}
                >
                  <DeleteOutlineOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {REQUIERE_REF.includes(d.tipo_dte) && (
              <Box sx={{ gridColumn: '1 / -1', pl: 1, display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                  Referencia:
                </Typography>
                <Select
                  size="small"
                  displayEmpty
                  value={d.referencia ? `${d.referencia.tipo_doc_ref}-${d.referencia.folio_ref}` : ''}
                  onChange={e => {
                    if (!e.target.value) { setDoc(idx, 'referencia', null); return; }
                    const de = docsEmitidos.find(x => `${x.tipo_dte}-${x.folio}` === e.target.value);
                    if (de) setDoc(idx, 'referencia', {
                      tipo_doc_ref: de.tipo_dte,
                      folio_ref: de.folio,
                      fecha_ref: de.fecha_emision?.slice(0, 10) || HOY,
                      razon_ref: 'CORRIGE MONTO',
                    });
                  }}
                  sx={{ fontSize: '0.75rem', minWidth: 200, height: 28 }}
                >
                  <MenuItem value=""><em>Sin referencia</em></MenuItem>
                  {docsEmitidos.map(de => (
                    <MenuItem key={`${de.tipo_dte}-${de.folio}`} value={`${de.tipo_dte}-${de.folio}`}>
                      T{de.tipo_dte} F{de.folio} — ${de.monto_total?.toLocaleString('es-CL')}
                    </MenuItem>
                  ))}
                </Select>
                {d.referencia && (
                  <TextField
                    size="small"
                    label="Razón"
                    value={d.referencia.razon_ref}
                    onChange={e => setDoc(idx, 'referencia', { ...d.referencia, razon_ref: e.target.value })}
                    sx={{ minWidth: 180 }}
                    inputProps={{ style: { fontSize: '0.75rem', padding: '3px 8px' } }}
                  />
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Contador y advertencia */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="caption" color={faltanDocs ? 'warning.main' : 'success.main'}>
          {prevEmitidos.length > 0
            ? `${prevEmitidos.length} ya enviados + ${docs.length} nuevos = ${totalDocs} total`
            : `${docs.length} documento${docs.length !== 1 ? 's' : ''}`}
          {faltanDocs && ` — faltan ${MIN_DOCS - totalDocs} para llegar a ${MIN_DOCS}`}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddOutlined />}
          onClick={agregarFila}
          sx={{ fontSize: '0.72rem' }}
        >
          Agregar
        </Button>
      </Box>

      {/* Resultado */}
      {(() => {
        const res = resultado ?? paso?.ultimo_resultado;
        if (!res) return null;
        return (
          <Box sx={{ mb: 1.5 }}>
            <Alert severity={res.ok ? 'success' : 'warning'} sx={{ mb: 1, py: 0.5 }}>
              {res.ok
                ? `${res.documentos?.filter(r => r.ok).length} documentos enviados al SII correctamente`
                : `${res.documentos?.filter(r => r.ok).length} enviados, ${res.documentos?.filter(r => !r.ok).length} con error`}
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontSize: '0.7rem', py: 0.4, color: 'text.secondary' } }}>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Folio</TableCell>
                  <TableCell>Glosa</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>TrackID / Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {res.documentos?.map((r, i) => (
                  <TableRow key={i} sx={{ bgcolor: r.ok ? 'success.50' : 'error.50' }}>
                    <TableCell sx={{ py: 0.4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {r.ok
                          ? <CheckCircleOutlined sx={{ fontSize: 13, color: 'success.main' }} />
                          : <ErrorOutlined sx={{ fontSize: 13, color: 'error.main' }} />}
                        <Typography variant="caption">T{r.tipo_dte}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.4 }}>
                      <Typography variant="caption" fontFamily="monospace">
                        {r.folio ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.4 }}>
                      <Typography variant="caption">{r.glosa}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.4 }}>
                      <Typography variant="caption">
                        {r.total != null ? `$${r.total.toLocaleString('es-CL')}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.4, maxWidth: 180 }}>
                      {r.trackid && (
                        <Typography variant="caption" fontFamily="monospace" color="success.dark">
                          {r.trackid}
                        </Typography>
                      )}
                      {r.error && (
                        <Tooltip title={r.error} placement="top">
                          <Typography variant="caption" color="error.main" sx={{
                            display: 'block', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170,
                          }}>
                            {r.error}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      })()}

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Acciones */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" variant="text" onClick={() => setMostrarForm(false)} disabled={loading}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={loading ? <CircularProgress size={14} /> : <SendOutlined />}
          onClick={handleEmitir}
          disabled={loading || hayErrores || faltanDocs}
        >
          {loading ? 'Emitiendo...' : `Emitir ${docs.length} nuevo${docs.length !== 1 ? 's' : ''}`}
        </Button>
      </Box>
    </Box>
  );
}
